import React, { useCallback } from 'react'
import { Button } from '@components'
import { FaCheckCircle, FaMinusCircle, FaTimesCircle } from 'react-icons/fa'
import { useTheme } from '@hooks'
import cn from 'classnames'
import { useTranslation } from 'react-i18next'
import {
  IInvoice,
  kraviaSetInvoiceAsCreditLoss,
  kraviaSetInvoiceAsPaid,
  kraviaWithdrawInvoice,
  toHttpError
} from '@query'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useBusinessContext } from '@context'
import { getApiErrorMessage } from '@utils'
import toast from 'react-hot-toast'
import {
  StyledButtonContainer,
  StyledFlexContainer,
  StyledFullHeightContainer,
  StyledRadio,
  StyledRadioDescription,
  StyledRadioGroup,
  StyledRadioHeader,
  StyledToastLink
} from '@pages/InvoicingPage/DebtCollection/trackerMessages/KraviaCommon.styled.tsx'

type StopAction = 'cancel' | 'markAsPaid' | 'creditLoss'

interface StopOption {
  key: StopAction
  Icon: React.ElementType
  color: string
  text: string
  description: string
}

interface Props {
  invoice: IInvoice
  handleOnClose: () => void
}

const messageWithLinks = (message: string) => {
  const parts = message.split(/(https?:\/\/\S+)/)
  if (parts.length === 1) return message
  return (
    <span>
      {parts.map((part, index) =>
        part.startsWith('http') ? (
          <StyledToastLink key={index} href={part} target="_blank" rel="noopener noreferrer">
            {part}
          </StyledToastLink>
        ) : (
          part
        )
      )}
    </span>
  )
}

export const StopDebtCollectionForm: React.FC<Props> = ({ invoice, handleOnClose }) => {
  const theme = useTheme()
  const { slug } = useBusinessContext()
  const [selected, setSelected] = React.useState<StopAction>(null)
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const mutationFunction = useCallback(() => {
    const params = { slug, invoiceId: invoice?.id }
    switch (selected) {
      case 'cancel':
        return kraviaWithdrawInvoice(params)
      case 'markAsPaid':
        return kraviaSetInvoiceAsPaid(params)
      case 'creditLoss':
        return kraviaSetInvoiceAsCreditLoss(params)
    }
  }, [slug, invoice?.id, selected])

  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: mutationFunction,
    ...{
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [slug, 'invoices', invoice?.id] }),
          queryClient.invalidateQueries({ queryKey: [slug, 'kravia_invoice_details', invoice?.id] })
        ])
        toast.success(t('general.success'))
        handleOnClose()
      },
      onError: error => {
        try {
          toast.error(messageWithLinks(getApiErrorMessage(toHttpError(error), t('general.error'))))
        } catch {
          toast.error(t('general.error'))
        }
      }
    }
  })

  const options: StopOption[] = [
    {
      key: 'cancel',
      Icon: FaTimesCircle,
      color: theme.colors.iconGray,
      text: t('invoicing.debtCollection.stopCollectionModal.cancelTitle'),
      description: t('invoicing.debtCollection.stopCollectionModal.cancelInfo')
    },
    {
      key: 'markAsPaid',
      Icon: FaCheckCircle,
      color: theme.colors.success,
      text: t('invoicing.debtCollection.stopCollectionModal.markAsPaidTitle'),
      description: t('invoicing.debtCollection.stopCollectionModal.markAsPaidInfo')
    },
    {
      key: 'creditLoss',
      Icon: FaMinusCircle,
      color: theme.colors.dashboardNeg,
      text: t('invoicing.debtCollection.stopCollectionModal.creditLossTitle'),
      description: t('invoicing.debtCollection.stopCollectionModal.creditLossInfo')
    }
  ]

  return (
    <StyledFlexContainer>
      <StyledFullHeightContainer>
        <StyledRadioGroup>
          {options.map(option => (
            <StyledRadio
              key={option.key}
              className={cn({ selected: selected === option.key })}
              onClick={() => setSelected(option.key)}
              color={option.color}
            >
              <StyledRadioHeader>
                <option.Icon />
                <span>{option.text}</span>
              </StyledRadioHeader>
              <StyledRadioDescription>{option.description}</StyledRadioDescription>
            </StyledRadio>
          ))}
        </StyledRadioGroup>
      </StyledFullHeightContainer>

      <StyledButtonContainer>
        <Button onClick={() => handleOnClose()}>{t('general.cancel')}</Button>
        <Button
          intent={'primary'}
          onClick={() => mutate()}
          showSpinner={isLoading}
          disabled={!selected || isLoading}
        >
          {t('general.save')}
        </Button>
      </StyledButtonContainer>
    </StyledFlexContainer>
  )
}
